import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import { DEPLOYMENT_TYPES, getPreset } from '../../config/deploymentPresets';
import DataTable from '../../components/DataTable/DataTable';
import './Tenants.css';
import DateInput from '../../components/DateInput/DateInput';
import { formatDate } from '../../utils/dateFormat';

const Tenants = () => {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [viewingTenant, setViewingTenant] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        companyName: '',
        type: 'enterprise',
        address: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        description: '',
        status: 'active',
        subscriptionPlan: 'basic',
        validTill: '',
        maxBranches: 1,
        maxUsers: 100
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await tenantAPI.getAll();
            setTenants(response.data.data?.tenants || response.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch tenants');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            name: formData.name,
            companyName: formData.companyName,
            type: formData.type,
            description: formData.description,
            status: formData.status,
            contactPerson: {
                name: formData.contactPerson,
                email: formData.contactEmail,
                mobile: formData.contactPhone
            },
            address: {
                street: formData.address
            },
            subscription: {
                plan: formData.subscriptionPlan,
                validTill: formData.validTill || undefined,
                maxBranches: parseInt(formData.maxBranches),
                maxUsers: parseInt(formData.maxUsers)
            }
        };

        try {
            if (editingTenant) {
                await tenantAPI.update(editingTenant._id, payload);
                toast.success('Tenant updated successfully');
            } else {
                await tenantAPI.create(payload);
                toast.success('Tenant created successfully');
            }
            setShowModal(false);
            setEditingTenant(null);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleEdit = (tenant) => {
        setEditingTenant(tenant);
        setFormData({
            name: tenant.name,
            companyName: tenant.companyName || '',
            type: tenant.type || 'enterprise',
            address: typeof tenant.address === 'object' ? tenant.address?.street : tenant.address || '',
            contactPerson: typeof tenant.contactPerson === 'object' ? tenant.contactPerson?.name : tenant.contactPerson || '',
            contactEmail: tenant.contactPerson?.email || tenant.contactEmail || '',
            contactPhone: tenant.contactPerson?.mobile || tenant.contactPhone || '',
            description: tenant.description || '',
            status: tenant.status,
            subscriptionPlan: tenant.subscription?.plan || 'basic',
            validTill: tenant.subscription?.validTill ? new Date(tenant.subscription.validTill).toISOString().split('T')[0] : '',
            maxBranches: tenant.subscription?.maxBranches || 1,
            maxUsers: tenant.subscription?.maxUsers || 100
        });
        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!tenantToDelete) return;
        try {
            await tenantAPI.delete(tenantToDelete._id);
            toast.success('Tenant deleted successfully');
            fetchData();
            setShowDeleteModal(false);
            setTenantToDelete(null);
        } catch (error) {
            toast.error('Failed to delete tenant');
        }
    };

    const handleDelete = (tenant) => {
        setTenantToDelete(tenant);
        setShowDeleteModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            companyName: '',
            type: 'enterprise',
            address: '',
            contactPerson: '',
            contactEmail: '',
            contactPhone: '',
            description: '',
            status: 'active',
            subscriptionPlan: 'basic',
            validTill: '',
            maxBranches: 1,
            maxUsers: 100
        });
    };

    // Organization type config
    const orgTypeConfig = {
        hotel: { icon: 'fa-hotel', label: 'Hotel', color: '#f59e0b' },
        isp: { icon: 'fa-wifi', label: 'ISP', color: '#3b82f6' },
        mall: { icon: 'fa-store', label: 'Mall', color: '#ec4899' },
        campus: { icon: 'fa-graduation-cap', label: 'Campus', color: '#10b981' },
        enterprise: { icon: 'fa-building', label: 'Enterprise', color: '#8b5cf6' },
        other: { icon: 'fa-globe', label: 'Other', color: '#6b7280' }
    };

    const getAddress = (t) => (typeof t.address === 'object' ? t.address?.street : t.address) || 'No address set';
    const getContactName = (t) => (typeof t.contactPerson === 'object' ? t.contactPerson?.name : t.contactPerson) || 'N/A';
    const getContactEmail = (t) => (typeof t.contactPerson === 'object' ? t.contactPerson?.email : t.contactEmail);
    const getContactPhone = (t) => (typeof t.contactPerson === 'object' ? t.contactPerson?.mobile : t.contactPhone);

    const getPlanColor = (plan) => {
        const colors = {
            free: 'gray',
            basic: 'blue',
            pro: 'purple',
            enterprise: 'gold'
        };
        return colors[plan] || 'gray';
    };

    const stats = {
        total: tenants.length,
        active: tenants.filter(t => t.status === 'active').length,
        inactive: tenants.filter(t => t.status === 'inactive').length,
        enterprise: tenants.filter(t => t.subscription?.plan === 'enterprise').length
    };

    return (
        <div className="page-container-modern">
            {/* Header Section */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-building"></i>
                            Tenant Management
                        </h1>
                        <p className="page-subtitle-modern">Manage organizations, subscriptions, and configurations</p>
                    </div>
                    <button className="btn-primary-modern" onClick={() => {
                        setEditingTenant(null);
                        resetForm();
                        setShowModal(true);
                    }}>
                        <i className="fas fa-plus-circle"></i>
                        Add New Tenant
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon blue">
                            <i className="fas fa-building"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.total}</span>
                            <span className="stat-label">Total Tenants</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon green">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.active}</span>
                            <span className="stat-label">Active</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon orange">
                            <i className="fas fa-pause-circle"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.inactive}</span>
                            <span className="stat-label">Inactive</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon purple">
                            <i className="fas fa-crown"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.enterprise}</span>
                            <span className="stat-label">Enterprise</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading tenants...</p>
                </div>
            ) : (
                <div className="ad-card">
                    <div className="ad-card-header">
                        <div className="ad-card-title">
                            <i className="fas fa-building"></i>
                            <h2>Tenants</h2>
                        </div>
                    </div>
                    <div style={{ padding: '0 1.375rem 1.375rem' }}>
                        <DataTable
                            emptyMessage="No tenants found"
                            columns={[
                                {
                                    label: 'Tenant', field: 'name', filter: true,
                                    render: (row) => {
                                        const cfg = orgTypeConfig[row.tenant.type || 'enterprise'] || orgTypeConfig.enterprise;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <i className={`fas ${cfg.icon}`} style={{ fontSize: '0.8rem' }}></i>
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{row.tenant.name}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.tenant.companyName || 'No company name'}</div>
                                                </div>
                                            </div>
                                        );
                                    },
                                    searchValue: (row) => `${row.tenant.name} ${row.tenant.companyName || ''} ${getAddress(row.tenant)}`,
                                },
                                {
                                    label: 'Type', field: 'typeLabel',
                                    filter: { options: Object.values(orgTypeConfig).map(c => c.label) },
                                    render: (row) => {
                                        const cfg = orgTypeConfig[row.tenant.type || 'enterprise'] || orgTypeConfig.enterprise;
                                        return (
                                            <span className="type-badge" style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                                                <i className={`fas ${cfg.icon}`}></i> {cfg.label}
                                            </span>
                                        );
                                    },
                                },
                                {
                                    label: 'Status', field: 'status',
                                    filter: { options: ['active', 'inactive', 'suspended'] },
                                    render: (row) => (
                                        <span className={`status-badge ${row.status}`}>
                                            <span className="badge-dot"></span>{row.status}
                                        </span>
                                    ),
                                },
                                {
                                    label: 'Plan', field: 'plan',
                                    filter: { options: ['free', 'basic', 'pro', 'enterprise'] },
                                    render: (row) => (
                                        <span className={`plan-badge ${getPlanColor(row.tenant.subscription?.plan)}`}>
                                            <i className="fas fa-crown"></i> {row.plan.toUpperCase()}
                                        </span>
                                    ),
                                },
                                { label: 'Branches / Users', field: 'capacityText', sortable: false },
                                {
                                    label: 'Action', field: 'action', sortable: false, searchValue: () => '',
                                    render: (row) => (
                                        <div className="table-row-actions">
                                            <button className="action-btn-icon view" onClick={() => setViewingTenant(row.tenant)} title="View"><i className="fas fa-eye"></i></button>
                                            <button className="action-btn-icon branding" onClick={() => navigate(`/tenants/${row.tenant._id}/profile`)} title="Branding & Profile"><i className="fas fa-palette"></i></button>
                                            <button className="action-btn-icon edit" onClick={() => handleEdit(row.tenant)} title="Edit"><i className="fas fa-edit"></i></button>
                                            <button className="action-btn-icon delete" onClick={() => handleDelete(row.tenant)} title="Delete"><i className="fas fa-trash"></i></button>
                                        </div>
                                    ),
                                },
                            ]}
                            rows={tenants.map((tenant) => ({
                                id: tenant._id,
                                tenant,
                                status: tenant.status,
                                plan: tenant.subscription?.plan || 'basic',
                                typeLabel: (orgTypeConfig[tenant.type || 'enterprise'] || orgTypeConfig.enterprise).label,
                                capacityText: `${tenant.subscription?.maxBranches || 1} branches · ${tenant.subscription?.maxUsers || 100} users`,
                            }))}
                        />
                    </div>
                </div>
            )}

            {/* Tenant Detail (View) Modal */}
            {viewingTenant && (
                <div className="modal-overlay-modern" onClick={() => setViewingTenant(null)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{viewingTenant.name}</h2>
                                <p className="modal-subtitle">{viewingTenant.companyName || 'No company name'}</p>
                            </div>
                            <button className="modal-close" onClick={() => setViewingTenant(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="status-badges">
                                <span className={`status-badge ${viewingTenant.status}`}>
                                    <span className="badge-dot"></span>{viewingTenant.status}
                                </span>
                                <span className="type-badge" style={{ background: `${orgTypeConfig[viewingTenant.type || 'enterprise']?.color}15`, color: orgTypeConfig[viewingTenant.type || 'enterprise']?.color, border: `1px solid ${orgTypeConfig[viewingTenant.type || 'enterprise']?.color}30` }}>
                                    <i className={`fas ${orgTypeConfig[viewingTenant.type || 'enterprise']?.icon}`}></i>
                                    {orgTypeConfig[viewingTenant.type || 'enterprise']?.label}
                                </span>
                                <span className={`plan-badge ${getPlanColor(viewingTenant.subscription?.plan)}`}>
                                    <i className="fas fa-crown"></i>
                                    {(viewingTenant.subscription?.plan || 'basic').toUpperCase()}
                                </span>
                            </div>

                            <div className="info-grid two-col">
                                <div className="info-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <div><span className="info-label">Address</span><span className="info-value">{getAddress(viewingTenant)}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-user-tie"></i>
                                    <div><span className="info-label">Contact Person</span><span className="info-value">{getContactName(viewingTenant)}</span></div>
                                </div>
                                {getContactEmail(viewingTenant) && (
                                    <div className="info-item">
                                        <i className="fas fa-envelope"></i>
                                        <div>
                                            <span className="info-label">Email</span>
                                            <a href={`mailto:${getContactEmail(viewingTenant)}`} className="info-value link">{getContactEmail(viewingTenant)}</a>
                                        </div>
                                    </div>
                                )}
                                {getContactPhone(viewingTenant) && (
                                    <div className="info-item">
                                        <i className="fas fa-phone"></i>
                                        <div><span className="info-label">Phone</span><span className="info-value">{getContactPhone(viewingTenant)}</span></div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <i className="fas fa-sitemap"></i>
                                    <div><span className="info-label">Max Branches</span><span className="info-value">{viewingTenant.subscription?.maxBranches || 1}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-users"></i>
                                    <div><span className="info-label">Max Users</span><span className="info-value">{viewingTenant.subscription?.maxUsers || 100}</span></div>
                                </div>
                                {viewingTenant.subscription?.validTill && (
                                    <div className="info-item">
                                        <i className="fas fa-calendar-check"></i>
                                        <div><span className="info-label">Valid Till</span><span className="info-value">{formatDate(viewingTenant.subscription.validTill)}</span></div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <i className="fas fa-calendar-alt"></i>
                                    <div><span className="info-label">Joined</span><span className="info-value">{formatDate(viewingTenant.createdAt)}</span></div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern" style={{ justifyContent: 'space-between' }}>
                            <button className="btn-secondary-modern" onClick={() => { const t = viewingTenant; setViewingTenant(null); navigate(`/tenants/${t._id}/profile`); }}>
                                <i className="fas fa-palette"></i> Branding & Profile
                            </button>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn-secondary-modern" onClick={() => setViewingTenant(null)}>Close</button>
                                <button className="btn-primary-modern" onClick={() => { const t = viewingTenant; setViewingTenant(null); handleEdit(t); }}>
                                    <i className="fas fa-edit"></i> Edit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {
                showModal && (
                    <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                        <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-modern">
                                <div>
                                    <h2>{editingTenant ? 'Edit Tenant' : 'Create New Tenant'}</h2>
                                    <p className="modal-subtitle">
                                        {editingTenant ? 'Update tenant information and subscription' : 'Add a new organization to the system'}
                                    </p>
                                </div>
                                <button className="modal-close" onClick={() => setShowModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body-modern">
                                    {/* Basic Information */}
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-info-circle"></i>
                                            Basic Information
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>Organization Name <span className="required">*</span></label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter organization name"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Company Name</label>
                                                <input
                                                    type="text"
                                                    name="companyName"
                                                    value={formData.companyName}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter company name"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Deployment Category <span className="required">*</span></label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={(e) => {
                                                    handleInputChange(e);
                                                    // Auto-load preset description when category changes
                                                    const preset = getPreset(e.target.value);
                                                    if (preset.description && !formData.description) {
                                                        handleInputChange({ target: { name: 'description', value: preset.description } });
                                                    }
                                                }}
                                            >
                                                {DEPLOYMENT_TYPES.map(dt => (
                                                    <option key={dt.category} value={dt.category}>
                                                        {dt.label}
                                                    </option>
                                                ))}
                                                <option value="isp">ISP / Internet Provider</option>
                                            </select>
                                        </div>
                                        {/* Deployment Subcategory */}
                                        {DEPLOYMENT_TYPES.find(dt => dt.category === formData.type) && (
                                            <div className="form-group-modern">
                                                <label>Deployment Type</label>
                                                <select
                                                    name="deploymentSubcategory"
                                                    value={formData.deploymentSubcategory || ''}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">— Select Specific Type —</option>
                                                    {(DEPLOYMENT_TYPES.find(dt => dt.category === formData.type)?.subcategories || []).map(sc => (
                                                        <option key={sc.value} value={sc.value}>{sc.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {/* Preset Info Banner */}
                                        {formData.type && getPreset(formData.type)?.loginMethods?.length > 0 && (
                                            <div style={{ gridColumn:'1/-1', background:'#ede9fe', border:'1px solid #c4b5fd', borderRadius:'10px', padding:'0.75rem 1rem', fontSize:'0.82rem', color:'#4f46e5' }}>
                                                <strong>Auto-loaded defaults:</strong> Login methods: {getPreset(formData.type).loginMethods.join(', ')} · Session: {getPreset(formData.type).sessionTimeout ? `${Math.round(getPreset(formData.type).sessionTimeout/3600)}h` : 'unlimited'} · Devices: {getPreset(formData.type).deviceLimit}
                                            </div>
                                        )}
                                        <div className="form-group-modern full-width">
                                            <label>Address <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                placeholder="Enter street address"
                                                required
                                            />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Enter description (optional)"
                                                rows="3"
                                            ></textarea>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Status</label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="suspended">Suspended</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Contact Information */}
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-address-book"></i>
                                            Contact Information
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>Contact Person <span className="required">*</span></label>
                                                <input
                                                    type="text"
                                                    name="contactPerson"
                                                    value={formData.contactPerson}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter contact person name"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Email <span className="required">*</span></label>
                                                <input
                                                    type="email"
                                                    name="contactEmail"
                                                    value={formData.contactEmail}
                                                    onChange={handleInputChange}
                                                    placeholder="email@example.com"
                                                    required
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Phone <span className="required">*</span></label>
                                                <input
                                                    type="tel"
                                                    name="contactPhone"
                                                    value={formData.contactPhone}
                                                    onChange={handleInputChange}
                                                    placeholder="+1 234 567 8900"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subscription */}
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-crown"></i>
                                            Subscription Details
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>Plan</label>
                                                <select
                                                    name="subscriptionPlan"
                                                    value={formData.subscriptionPlan}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="free">Free</option>
                                                    <option value="basic">Basic</option>
                                                    <option value="pro">Pro</option>
                                                    <option value="enterprise">Enterprise</option>
                                                </select>
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Valid Until</label>
                                                <DateInput
                                                    type="date"
                                                    name="validTill"
                                                    value={formData.validTill}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Max Branches</label>
                                                <input
                                                    type="number"
                                                    name="maxBranches"
                                                    value={formData.maxBranches}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Max Users</label>
                                                <input
                                                    type="number"
                                                    name="maxUsers"
                                                    value={formData.maxUsers}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hotel-Specific Features — shown only when type = hotel */}
                                    {formData.type === 'hotel' && (
                                        <div className="form-section isp-features-section hotel-features-section">
                                            <h3 className="section-title">
                                                <i className="fas fa-hotel"></i>
                                                Hotel Features
                                                <span className="section-badge hotel-badge">Hotel Only</span>
                                            </h3>

                                            <div className="isp-features-grid">
                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                                        <i className="fas fa-door-open"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Guest Check-in / Check-out</h4>
                                                        <p>Manage room occupancy. Check guests in/out directly from the admin panel — internet access follows room status automatically.</p>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                                                        <i className="fas fa-sync-alt"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>PMS Integration</h4>
                                                        <p>Connect to your Property Management System (Opera, Protel, Staah, etc.) for automatic guest data sync and access control.</p>
                                                        <div className="payment-gateways-list">
                                                            <span className="gateway-badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #1d4ed8' }}>Opera</span>
                                                            <span className="gateway-badge" style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #7c3aed' }}>Protel</span>
                                                            <span className="gateway-badge" style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #059669' }}>Staah</span>
                                                            <span className="gateway-badge" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #c2410c' }}>Custom</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon" style={{ background: 'linear-gradient(135deg, #ec4899, #be185d)' }}>
                                                        <i className="fas fa-wifi"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Captive Portal Authentication</h4>
                                                        <p>Guests authenticate via Room Number + Last Name or OTP. Internet auto-grants on check-in, revokes on check-out.</p>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                                                        <i className="fas fa-chart-bar"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Occupancy Dashboard</h4>
                                                        <p>Real-time view of occupied, available, cleaning, and maintenance rooms. Track bandwidth per room and per guest.</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="isp-config-note">
                                                <i className="fas fa-info-circle"></i>
                                                <span>
                                                    After saving, manage rooms from <strong>Hotel → Rooms</strong> in the sidebar.
                                                    Configure PMS integration from <strong>Branches → Edit → Integrations</strong>.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* ISP-Specific Features — shown only when type = isp */}
                                    {formData.type === 'isp' && (
                                        <div className="form-section isp-features-section">
                                            <h3 className="section-title">
                                                <i className="fas fa-wifi"></i>
                                                ISP Features
                                                <span className="section-badge">ISP Only</span>
                                            </h3>

                                            <div className="isp-features-grid">
                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon payment">
                                                        <i className="fas fa-credit-card"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Payment Gateway</h4>
                                                        <p>Integrated payment collection — users pay for plans before getting internet access.</p>
                                                        <div className="payment-gateways-list">
                                                            <span className="gateway-badge cashfree">Cashfree</span>
                                                            <span className="gateway-badge razorpay">Razorpay</span>
                                                            <span className="gateway-badge phonepe">PhonePe</span>
                                                            <span className="gateway-badge paytm">Paytm</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon plans">
                                                        <i className="fas fa-list-alt"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Plan-Based Access</h4>
                                                        <p>Users select from available internet plans (speed/data/time limits) at the captive portal.</p>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon radius">
                                                        <i className="fas fa-network-wired"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>RADIUS Integration</h4>
                                                        <p>Automatic RADIUS account creation upon payment. Bandwidth & session limits enforced by NAS.</p>
                                                    </div>
                                                </div>

                                                <div className="isp-feature-card">
                                                    <div className="isp-feature-icon orders">
                                                        <i className="fas fa-receipt"></i>
                                                    </div>
                                                    <div className="isp-feature-content">
                                                        <h4>Order Management</h4>
                                                        <p>Track orders, payments, refunds, and subscription validity from the admin panel.</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="isp-config-note">
                                                <i className="fas fa-info-circle"></i>
                                                <span>
                                                    After saving, configure payment gateways from <strong>Settings → Payment Gateways</strong>.
                                                    Plans with pricing can be managed from the <strong>Plans</strong> section.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-footer-modern">
                                    <button
                                        type="button"
                                        className="btn-secondary-modern"
                                        onClick={() => setShowModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary-modern">
                                        <i className="fas fa-check"></i>
                                        {editingTenant ? 'Update Tenant' : 'Create Tenant'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && (
                    <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                        <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-modern danger">
                                <div>
                                    <h2>Delete Tenant</h2>
                                    <p className="modal-subtitle">This action cannot be undone</p>
                                </div>
                                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="modal-body-modern">
                                <div className="warning-box">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <div>
                                        <p>Are you sure you want to delete <strong>{tenantToDelete?.name}</strong>?</p>
                                        <p className="warning-text">
                                            All associated data, users, and configurations will be permanently deleted.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-modern">
                                <button
                                    className="btn-secondary-modern"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button className="btn-danger-modern" onClick={confirmDelete}>
                                    <i className="fas fa-trash"></i>
                                    Delete Tenant
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Tenants;